import React, { useState, useCallback, useEffect } from 'react';
import {
  Info,
  Building2,
  Trash2,
  RefreshCw,
  Download,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ArcheHeader } from '../../../components/ArcheHeader';
import { usePlaidLink } from '../hooks/usePlaidLink';
import { api, BankingConnectionSummary, BankingExportResponse } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { traceStart, traceEnd, traceError } from '@/lib/telemetry';

// Arche ID for bank-statement-download
const ARCHE_ID = 'arche_bank_statement_download';

// Info tooltip component for explaining format options
function InfoTooltip({ children }: { children: React.ReactNode }) {
  return (
    <div className="group relative inline-flex items-center">
      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
      <div className="absolute left-6 top-0 z-50 hidden group-hover:block w-64 p-2 text-xs bg-popover text-popover-foreground rounded-md shadow-lg border border-border">
        {children}
      </div>
    </div>
  );
}

// Bank connection card component
function BankConnectionCard({
  connection,
  onSync,
  onDelete,
  isSyncing,
}: {
  connection: BankingConnectionSummary;
  onSync: () => void;
  onDelete: () => void;
  isSyncing: boolean;
}) {
  return (
    <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Building2 className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="font-medium">{connection.institutionName}</p>
          <p className="text-sm text-muted-foreground">
            {connection.accountName} ••••{connection.accountMask}
          </p>
          {connection.lastSyncedAt && (
            <p className="text-xs text-muted-foreground">
              Last synced: {new Date(connection.lastSyncedAt).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onSync}
          disabled={isSyncing}
          title="Sync transactions"
        >
          <RefreshCw className={cn('h-4 w-4', isSyncing && 'animate-spin')} />
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              title="Remove connection"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove bank connection?</AlertDialogTitle>
              <AlertDialogDescription>
                This will disconnect {connection.institutionName} ({connection.accountName}) and
                remove all synced transaction data. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={onDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

// Inner component that uses the SearchResults context
function BankStatementDownloadContent() {
  // Banking state
  const [bankingConfigured, setBankingConfigured] = useState<boolean | null>(null);
  const [connections, setConnections] = useState<BankingConnectionSummary[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [syncingConnectionId, setSyncingConnectionId] = useState<string | null>(null);

  // Form state
  const [selectedConnectionId, setSelectedConnectionId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>(() => {
    // Default to 30 days ago
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    // Default to today
    return new Date().toISOString().split('T')[0];
  });
  const [exportCsv, setExportCsv] = useState(true);
  const [exportPdf, setExportPdf] = useState(true);

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState<BankingExportResponse | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // Plaid Link hook
  const {
    open: openPlaidLink,
    ready: plaidReady,
    isLoading: plaidLoading,
    error: plaidError,
    refreshLinkToken,
  } = usePlaidLink({
    onSuccess: async (publicToken, metadata) => {
      const traceId = traceStart('exchangeBankingToken', {
        institution: metadata.institution?.name,
      });
      try {
        // Exchange token with backend
        const result = await api.exchangeBankingToken({
          publicToken,
          institutionId: metadata.institution?.institution_id,
          accounts: metadata.accounts.map((acc) => ({
            id: acc.id,
            name: acc.name,
            mask: acc.mask,
            type: acc.type,
            subtype: acc.subtype,
          })),
        });

        traceEnd(traceId, { connectionCount: result.connections.length });
        toast({
          title: 'Bank account connected',
          description: `Successfully connected to ${metadata.institution?.name || 'your bank'}`,
        });

        // Refresh connections list
        await loadConnections();

        // Auto-select the first new connection
        if (result.connections.length > 0) {
          setSelectedConnectionId(result.connections[0].connectionId);
        }
      } catch (err) {
        traceError(traceId, err instanceof Error ? err : new Error(String(err)));
        toast({
          title: 'Connection failed',
          description: 'Failed to connect bank account. Please try again.',
          variant: 'destructive',
        });
      }
    },
    onExit: (error) => {
      if (error) {
        const traceId = traceStart('plaidLinkExit');
        traceError(
          traceId,
          new Error(error.display_message || error.error_code || 'Unknown Plaid error'),
        );
      }
    },
  });

  // Check banking status and load connections on mount
  useEffect(() => {
    checkBankingStatus();
    loadConnections();
  }, []);

  const checkBankingStatus = async () => {
    const traceId = traceStart('checkBankingStatus');
    try {
      const status = await api.getBankingStatus();
      traceEnd(traceId, { configured: status.configured });
      setBankingConfigured(status.configured);
    } catch (err) {
      traceError(traceId, err instanceof Error ? err : new Error(String(err)));
      setBankingConfigured(false);
    }
  };

  const loadConnections = async () => {
    const traceId = traceStart('loadBankConnections');
    try {
      setLoadingConnections(true);
      const result = await api.listBankingConnections();
      setConnections(result.connections);

      // Auto-select first connection if none selected
      if (result.connections.length > 0 && !selectedConnectionId) {
        setSelectedConnectionId(result.connections[0].connectionId);
      }
      traceEnd(traceId, { count: result.connections.length });
    } catch (err) {
      traceError(traceId, err instanceof Error ? err : new Error(String(err)));
      toast({
        title: 'Error',
        description: 'Failed to load bank connections',
        variant: 'destructive',
      });
    } finally {
      setLoadingConnections(false);
    }
  };

  const handleSyncConnection = useCallback(async (connectionId: string) => {
    const traceId = traceStart('syncBankConnection', { connectionId });
    try {
      setSyncingConnectionId(connectionId);
      const result = await api.syncBankingConnection(connectionId);
      const totalSynced = result.transactionsAdded + result.transactionsUpdated;
      traceEnd(traceId, { totalSynced });
      toast({
        title: 'Sync complete',
        description: `Synced ${totalSynced} transactions`,
      });
      // Refresh connections to update last synced date
      await loadConnections();
    } catch (err) {
      traceError(traceId, err instanceof Error ? err : new Error(String(err)));
      toast({
        title: 'Sync failed',
        description: 'Failed to sync transactions. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSyncingConnectionId(null);
    }
  }, []);

  const handleDeleteConnection = useCallback(
    async (connectionId: string) => {
      const traceId = traceStart('deleteBankConnection', { connectionId });
      try {
        await api.deleteBankingConnection(connectionId);
        traceEnd(traceId);
        toast({
          title: 'Connection removed',
          description: 'Bank connection has been removed',
        });

        // Refresh connections
        await loadConnections();

        // Clear selection if deleted connection was selected
        if (selectedConnectionId === connectionId) {
          setSelectedConnectionId('');
        }
      } catch (err) {
        traceError(traceId, err instanceof Error ? err : new Error(String(err)));
        toast({
          title: 'Error',
          description: 'Failed to remove bank connection',
          variant: 'destructive',
        });
      }
    },
    [selectedConnectionId],
  );

  const handleExport = async () => {
    if (!selectedConnectionId) {
      toast({
        title: 'Select a bank account',
        description: 'Please select a bank account to export statements from',
        variant: 'destructive',
      });
      return;
    }

    if (!exportCsv && !exportPdf) {
      toast({
        title: 'Select a format',
        description: 'Please select at least one export format (CSV or PDF)',
        variant: 'destructive',
      });
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      toast({
        title: 'Invalid date range',
        description: 'Start date must be before end date',
        variant: 'destructive',
      });
      return;
    }

    const traceId = traceStart('exportBankStatement', {
      formats: [exportCsv && 'csv', exportPdf && 'pdf'].filter(Boolean),
    });
    try {
      setIsExporting(true);
      setExportSuccess(null);
      setExportError(null);

      const formats: ('csv' | 'pdf')[] = [];
      if (exportCsv) formats.push('csv');
      if (exportPdf) formats.push('pdf');

      const result = await api.exportBankingStatement({
        connectionId: selectedConnectionId,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        formats,
      });

      traceEnd(traceId, {
        fileCount: result.files.length,
        transactionCount: result.transactionCount,
      });
      setExportSuccess(result);
      toast({
        title: 'Export complete',
        description: `Statement saved to My-Fias/Financial folder`,
      });
    } catch (err) {
      traceError(traceId, err instanceof Error ? err : new Error(String(err)));
      const errorMessage = err instanceof Error ? err.message : 'Export failed. Please try again.';
      setExportError(errorMessage);
      toast({
        title: 'Export failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId={ARCHE_ID} title="Bank Statement Download" />

      {/* Main content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">
          {/* Banking not configured warning */}
          {bankingConfigured === false && (
            <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-600 dark:text-yellow-400">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                <p className="font-medium">Banking integration is not configured</p>
              </div>
              <p className="text-sm mt-1">
                Please contact your administrator to set up Plaid API credentials.
              </p>
            </div>
          )}

          {/* Section: Connect Bank Account */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Connected Accounts</h2>
              <Button
                onClick={openPlaidLink}
                disabled={!plaidReady || plaidLoading || bankingConfigured === false}
              >
                {plaidLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Building2 className="h-4 w-4 mr-2" />
                    Connect Bank Account
                  </>
                )}
              </Button>
            </div>

            {plaidError && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                <p>{plaidError.message}</p>
                <Button variant="link" size="sm" onClick={refreshLinkToken} className="p-0 h-auto">
                  Retry
                </Button>
              </div>
            )}

            {/* Connections list */}
            {loadingConnections ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : connections.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No bank accounts connected</p>
                <p className="text-sm">Connect a bank account to download statements</p>
              </div>
            ) : (
              <div className="space-y-3">
                {connections.map((connection) => (
                  <BankConnectionCard
                    key={connection.connectionId}
                    connection={connection}
                    onSync={() => handleSyncConnection(connection.connectionId)}
                    onDelete={() => handleDeleteConnection(connection.connectionId)}
                    isSyncing={syncingConnectionId === connection.connectionId}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Section: Download Statement */}
          {connections.length > 0 && (
            <section className="space-y-6 border-t border-border pt-8">
              <h2 className="text-xl font-semibold">Download Statement</h2>

              {/* Account selector */}
              <div className="space-y-2">
                <Label htmlFor="account-select">Bank Account</Label>
                <Select value={selectedConnectionId} onValueChange={setSelectedConnectionId}>
                  <SelectTrigger id="account-select">
                    <SelectValue placeholder="Select an account" />
                  </SelectTrigger>
                  <SelectContent>
                    {connections.map((connection) => (
                      <SelectItem key={connection.connectionId} value={connection.connectionId}>
                        {connection.institutionName} - {connection.accountName} ••••
                        {connection.accountMask}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start-date">Start Date</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-date">End Date</Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              {/* Format checkboxes */}
              <div className="space-y-3">
                <Label>Export Format</Label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="export-csv"
                      checked={exportCsv}
                      onCheckedChange={(checked) => setExportCsv(checked === true)}
                    />
                    <Label htmlFor="export-csv" className="cursor-pointer font-normal">
                      CSV
                    </Label>
                    <InfoTooltip>
                      <strong>CSV Format</strong>
                      <br />
                      Best for automation and importing into spreadsheets like Excel or Google
                      Sheets. Easy to process with scripts or financial software.
                    </InfoTooltip>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="export-pdf"
                      checked={exportPdf}
                      onCheckedChange={(checked) => setExportPdf(checked === true)}
                    />
                    <Label htmlFor="export-pdf" className="cursor-pointer font-normal">
                      PDF
                    </Label>
                    <InfoTooltip>
                      <strong>PDF Format</strong>
                      <br />
                      Best for visual reading and record-keeping. Formatted for printing and
                      archival purposes.
                    </InfoTooltip>
                  </div>
                </div>
              </div>

              {/* Export button */}
              <Button
                onClick={handleExport}
                disabled={isExporting || !selectedConnectionId || (!exportCsv && !exportPdf)}
                className="w-full"
                size="lg"
              >
                {isExporting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Download Statement
                  </>
                )}
              </Button>

              {/* Export success message */}
              {exportSuccess && (
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5" />
                    <p className="font-medium">Statement exported successfully!</p>
                  </div>
                  <p className="text-sm mt-1">
                    Your statement has been saved to the <strong>My-Fias/Financial</strong> folder.
                  </p>
                  <Link
                    to="/files"
                    className="text-sm underline mt-2 inline-block hover:no-underline"
                  >
                    View in My Files
                  </Link>
                </div>
              )}

              {/* Export error message */}
              {exportError && (
                <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    <p className="font-medium">Export failed</p>
                  </div>
                  <p className="text-sm mt-1">{exportError}</p>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export function BankStatementDownloadPage() {
  return <BankStatementDownloadContent />;
}

export default BankStatementDownloadPage;
